import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { usersApi } from "../../api";
import {
  APP_ROUTES,
  ERROR_MESSAGES,
  IAuthResponse,
  IResponseError,
} from "../../data-access";
import { storeAuthActions } from "../../store/slices/auth";
import { storeBillingActions } from "../../store/slices/billing";
import {
  cookie,
  encryption,
  handleFailedRequest,
  updateExtensionCookies,
  getStoredUtm,
} from "../../util";
import { syncAuthWithExtension } from "../../util/auth-sync";

export const Auth = (): null => {
  const navigate = useNavigate();

  const dispatch = useDispatch();

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const email = queryParams.get("email");
    const accessCode = queryParams.get("access_code");
    const isRegistration = queryParams.get("isRegistration") === "1";
    if (!email || !accessCode) {
      navigate(APP_ROUTES.login);
    } else {
      usersApi
        .checkAccessCodeValidity({ email, access_code: accessCode })
        .then((response: IAuthResponse) => {
          const authDetails = {
            access_token: response.access_token,
            refresh_token: response.refresh_token,
            user: response.user,
          };

          dispatch(storeAuthActions.setUserData(authDetails));
          syncAuthWithExtension("AUTH_LOGIN", {
            accessToken: response.access_token,
            refreshToken: response.refresh_token,
            user: response.user,
          });

          if (response.user.promocode) {
            dispatch(
              storeBillingActions.update({ discount: response.user.promocode })
            );
          }

          cookie.set("exSM", encryption.generateHash("authorized"));
          updateExtensionCookies(authDetails);

          // GA4 funnel events fired at the moment of actual auth success.
          // `isRegistration=1` query param distinguishes a fresh sign-up
          // (account just created) from a returning login. UTMs captured
          // at first landing visit ride along so we can attribute the
          // conversion back to its original traffic source.
          const w = window as any;
          const utmParams = getStoredUtm();
          if (w.gtag) {
            if (isRegistration) {
              w.gtag("event", "sign_up", { method: "email", ...utmParams });
            }
            w.gtag("event", "login", { method: "email" });
          }
          // Persist first-touch attribution (gclid + UTMs) server-side so
          // paid conversions can be uploaded to Google Ads later. Fire and
          // forget - a failure must never break the auth flow.
          if (isRegistration && Object.keys(utmParams).length > 0) {
            usersApi.saveAttribution(utmParams).catch(() => undefined);
          }

          navigate(APP_ROUTES.dashboard);
        })
        .catch((error: IResponseError) => {
          navigate(APP_ROUTES.login);
          handleFailedRequest(
            error.status,
            ERROR_MESSAGES.AUTH_FAIL,
            error?.details,
            error.text
          );
        });
    }
  }, [navigate, dispatch]);

  return null;
};
